import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import useQueryParams from 'hooks/UseQueryParams';
import { ProjectionFields } from 'pages/digitalRetailing/DigitalRetailingConstant';
import { formatWithCommaSeparator } from 'pages/tradeIn/TradeInUtils';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { vehicleDetailsByVIN } from 'store/DigitalRetailingState';
import { RootState } from 'store/store';
import Clock from '../../icons/clock.svg';
import Share from '../../icons/share.svg';
import timerSingleton from '../../TimerSingleton';

const VehicleDetails: React.FC = () => {
  const { t } = useTranslation();

  const queryParams = useQueryParams();

  const { watch } = useFormContext();

  const payTypeWatcher = watch('payType');
  const downPaymentWatcher = watch('downPayment');
  const termWatcher = watch('term');

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  const { vehicleData, paymentData } = useSelector((state: RootState) => state.digitalRetailingStore);

  const term = authenticateClientData?.data?.widgetData?.term;
  const apr = authenticateClientData?.data?.widgetData?.apr;

  const _VIN = queryParams.get('VIN');

  useEffect(() => {
    if (vehicleData?.VIN) return;
    dispatch(
      vehicleDetailsByVIN({
        requestParams: {
          type: 'car',
          projection: ProjectionFields,
          featureWithDetails: true,
        },
        VIN: _VIN || '',
      })
    );
  }, []);

  const [timeLeft, setTimeLeft] = useState(timerSingleton.timeLeft);

  useEffect(() => {
    const updateTimer = (newTimeLeft: number) => setTimeLeft(newTimeLeft);

    // Subscribe to timer updates
    timerSingleton.subscribe(updateTimer);

    // Start the timer if it's not already running
    timerSingleton.startTimer();

    // Cleanup: Unsubscribe on component unmount
    return () => timerSingleton.unsubscribe(updateTimer);
  }, []);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0');
    const mins = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${hrs} : ${mins} : ${secs}`;
  };

  return (
    <aside className='dr-widget-steps__sidebar'>
      <div className='dr-car-info'>
        <div className='card dr-car-info__top'>
          {/* <button type='button' className='btn share'>
            <img src={Share} alt='Share' />
            {t('digitalRetailing.share')}
          </button> */}
          {vehicleData?.Images?.length > 0 ? (
            <div className='card__img-box'>
              <img src={vehicleData?.Images?.[0]?.sizes?.[0]?.path} alt='Car' className='w-100' />
            </div>
          ) : null}
          <div className='card__body'>
            <h4 className='card__title'>{`${vehicleData?.Year} ${vehicleData?.Make} ${vehicleData?.Model} ${vehicleData?.Trim}`}</h4>
            <p className='mb-0 text-secondary'>
              <span className='lbl'>VIN:</span> {vehicleData?.VIN}
            </p>
            <p className='mb-0 text-secondary'>
              <span className='lbl'>Stock:</span> {vehicleData?.StockNumber}
            </p>
            <hr />
            {vehicleData?.MSR ? (
              <div className='dr-car-info__total'>
                <span className='dr-car-info__total__lbl text-secondary'>MSRP</span>
                <span className='dr-car-info__total__value'>{`$${formatWithCommaSeparator(vehicleData?.MSR)}`}</span>
              </div>
            ) : null}

            {payTypeWatcher ? (
              <div className='dr-car-info__total'>
                <span className='dr-car-info__total__lbl text-secondary'>Payment Method</span>
                <span className='dr-car-info__total__value'>{payTypeWatcher}</span>
              </div>
            ) : null}

            {downPaymentWatcher ? (
              <div className='dr-car-info__total'>
                <span className='dr-car-info__total__lbl text-secondary'>Down Payment</span>
                <span className='dr-car-info__total__value'>{`$${formatWithCommaSeparator(downPaymentWatcher)}`}</span>
              </div>
            ) : null}

            {apr ? (
              <div className='dr-car-info__total'>
                <span className='dr-car-info__total__lbl text-secondary'>APR</span>
                <span className='dr-car-info__total__value'>{`${apr}%`}</span>
              </div>
            ) : null}

            {termWatcher || term ? (
              <div className='dr-car-info__total'>
                <span className='dr-car-info__total__lbl text-secondary'>Term</span>
                <span className='dr-car-info__total__value'>{`${termWatcher || term} ${t(
                  'digitalRetailing.months'
                )}`}</span>
              </div>
            ) : null}
            {paymentData?.monthly ? (
              <div className='dr-car-info__total'>
                <span className='dr-car-info__total__lbl text-secondary'>Monthly Payment</span>
                <span className='dr-car-info__total__value'>{`$${paymentData?.monthly?.toFixed(2)}`}</span>
              </div>
            ) : null}
          </div>
        </div>
        <div className='card dr-car-info__bottom'>
          <div className='card__body'>
            <div className='time-save'>
              <div className='time-save__icon'>
                <img src={Clock} alt='Clock' />
              </div>
              <div className='time-save__content'>
                <label className='time-save__lbl'>{'Time Save at Dealership'}</label>
                <div className='time-save__timer'>{formatTime(timeLeft)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default VehicleDetails;
